"use client";

import { useCallback } from 'react';
// @ts-expect-error FIXME(strictNullChecks)
import tabbable from 'tabbable';
import { isComposingEvent } from 'utils/is-composing';

/**
 * Override the default browser tabbing behavior to ensure that
 * tabbing remains localized to a specific element. This is particularly
 * useful for supporting accessibility in components like
 * modals.
 */

export function useFocusTrap(parentRef: any) {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Tab') {
        // Let IME composition (e.g. CJK candidate windows) consume Tab natively
        // instead of trapping focus, mirroring the Escape guard in DialogContainer.
        if (isComposingEvent(e)) return;
        e.preventDefault();
        e.stopPropagation();

        // determine the sequence of tabbable elements contained
        // within our parent
        const sequence = tabbable(parentRef.current);

        if (sequence.length === 0) {
          return;
        }

        let currentIndex = 0;

        const currentlyFocused = sequence.some(
          (element: HTMLElement, index: number) => {
            // eslint-disable-next-line react-hooks-ssr/react-hooks-global-ssr
            if (element === document.activeElement) {
              currentIndex = index;
              return true;
            }
            return false;
          }
        );

        if (!currentlyFocused) {
          sequence[currentIndex].focus();
          return;
        }

        const backwards = e.shiftKey;
        const nextIndex = backwards ? currentIndex - 1 : currentIndex + 1;
        const nextToFocus = sequence[nextIndex];

        if (nextToFocus) {
          nextToFocus.focus();
          return;
        }

        if (backwards) {
          sequence[sequence.length - 1].focus();
        } else {
          sequence[0].focus();
        }
      }
    },
    [parentRef]
  );

  return onKeyDown;
}
